import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import {
  FaBuilding,
  FaFileExcel,
  FaSearch,
  FaSpinner,
  FaStar,
  FaThumbsUp,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../services/axios";
import exportToExcel from "../../services/exportExcel";

function normalizarTexto(texto) {
  return String(texto || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function labelTipo(tipo) {
  if (tipo === "mais_quero" || tipo === "mais_quero_conversar") {
    return "Prioritário";
  }
  if (tipo === "quero" || tipo === "quero_conversar") {
    return "Desejável";
  }
  return tipo || "—";
}

function labelStatus(status) {
  const valor = String(status || "")
    .trim()
    .toLowerCase();
  if (valor === "confirmado") return "Confirmado";
  if (valor === "recusado") return "Recusado";
  if (valor === "pendente" || !valor) return "Pendente";
  return status;
}

function ehPrioritario(tipo) {
  return tipo === "mais_quero" || tipo === "mais_quero_conversar";
}

/**
 * Modal com todas as escolhas dos patrocinadores (lista + download Excel).
 * Ordenação da API: patrocinador → empresa do convidado.
 */
const ModalTodasEscolhasPatrocinadores = ({ show, onHide }) => {
  const [escolhas, setEscolhas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filtro, setFiltro] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/patrocinador-escolhas-todas");
      setEscolhas(
        Array.isArray(response.data?.dados) ? response.data.dados : [],
      );
    } catch (err) {
      console.error("Erro ao carregar escolhas:", err);
      setError(
        err?.response?.data?.mensagem ||
          "Erro ao carregar as escolhas dos patrocinadores",
      );
      setEscolhas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      setFiltro("");
      carregar();
    }
  }, [show, carregar]);

  const escolhasFiltradas = useMemo(() => {
    const termo = normalizarTexto(filtro);
    if (!termo) return escolhas;
    return escolhas.filter((item) => {
      const blob = normalizarTexto(
        [
          item.nome_patrocinador,
          item.nome_convidado,
          item.empresa_convidado,
          item.cargo_convidado,
          labelTipo(item.tipo),
          labelStatus(item.status),
        ].join(" "),
      );
      return blob.includes(termo);
    });
  }, [escolhas, filtro]);

  const grupos = useMemo(() => {
    const mapa = new Map();
    for (const item of escolhasFiltradas) {
      const key = item.id_patrocinador || item.nome_patrocinador;
      if (!mapa.has(key)) {
        mapa.set(key, {
          id: item.id_patrocinador,
          nome: item.nome_patrocinador,
          itens: [],
        });
      }
      mapa.get(key).itens.push(item);
    }
    return [...mapa.values()];
  }, [escolhasFiltradas]);

  const baixarExcel = useCallback(() => {
    if (!escolhasFiltradas.length) {
      toast.info("Não há escolhas para exportar.");
      return;
    }

    const linhas = escolhasFiltradas.map((item) => ({
      Patrocinador: item.nome_patrocinador || "",
      "Empresa do convidado": item.empresa_convidado || "",
      Convidado: item.nome_convidado || "",
      Cargo: item.cargo_convidado || "",
      Lista: labelTipo(item.tipo),
      Sequência: item.sequencia ?? "",
      Status: labelStatus(item.status),
      Ano: item.ano || "",
    }));

    const dataRef = new Date().toISOString().slice(0, 10);
    exportToExcel(linhas, `escolhas_patrocinadores_${dataRef}`);
    toast.success("Download iniciado.");
  }, [escolhasFiltradas]);

  const handleHide = () => {
    if (loading) return;
    setFiltro("");
    setError("");
    onHide?.();
  };

  return (
    <Modal
      show={show}
      onHide={handleHide}
      centered
      size="xl"
      className="apl-escolhas-todas-modal"
      backdrop={loading ? "static" : true}
    >
      <Modal.Header closeButton={!loading}>
        <Modal.Title>Escolhas dos patrocinadores</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="apl-escolhas-todas-toolbar">
          <div className="apl-search">
            <span className="apl-search-icon" aria-hidden>
              <FaSearch size={14} />
            </span>
            <input
              type="search"
              className="apl-search-input"
              placeholder="Buscar patrocinador, convidado ou empresa…"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              disabled={loading}
              aria-label="Filtrar escolhas"
            />
          </div>
          <div className="apl-escolhas-todas-meta">
            <span>
              {escolhasFiltradas.length} escolha
              {escolhasFiltradas.length === 1 ? "" : "s"}
              {grupos.length > 0
                ? ` · ${grupos.length} patrocinador${grupos.length === 1 ? "" : "es"}`
                : ""}
            </span>
            <button
              type="button"
              className="apl-btn apl-btn--secondary"
              onClick={baixarExcel}
              disabled={loading || escolhasFiltradas.length === 0}
              title="Baixar Excel com as escolhas listadas"
            >
              <FaFileExcel size={14} aria-hidden />
              Download Excel
            </button>
          </div>
        </div>

        {loading ? (
          <div className="apl-escolhas-todas-loading" role="status">
            <FaSpinner className="fa-spin" size={22} aria-hidden />
            <span>Carregando escolhas…</span>
          </div>
        ) : error ? (
          <div className="apl-alert" role="alert">
            {error}
          </div>
        ) : grupos.length === 0 ? (
          <div className="apl-empty">
            <p className="apl-empty-title">Nenhuma escolha encontrada</p>
            <p>
              {filtro
                ? "Ajuste a busca para ver outras escolhas."
                : "Ainda não há escolhas registradas pelos patrocinadores."}
            </p>
          </div>
        ) : (
          <div className="apl-escolhas-todas-lista">
            {grupos.map((grupo) => (
              <section
                key={grupo.id || grupo.nome}
                className="apl-escolhas-todas-grupo"
              >
                <header className="apl-escolhas-todas-grupo-head">
                  <FaBuilding aria-hidden />
                  <h3>{grupo.nome}</h3>
                  <span>{grupo.itens.length}</span>
                </header>
                <ul className="apl-escolhas-todas-itens">
                  {grupo.itens.map((item) => {
                    const prioritario = ehPrioritario(item.tipo);
                    return (
                      <li
                        key={
                          item.id ||
                          `${item.id_patrocinador}-${item.id_usuario}-${item.tipo}-${item.sequencia}`
                        }
                        className="apl-escolhas-todas-item"
                      >
                        <div className="apl-escolhas-todas-item-main">
                          <div className="apl-escolhas-todas-item-nome">
                            <FaUser aria-hidden />
                            <strong>{item.nome_convidado}</strong>
                            {item.sequencia ? (
                              <span className="apl-escolhas-todas-seq">
                                {item.sequencia}º
                              </span>
                            ) : null}
                          </div>
                          <div className="apl-escolhas-todas-item-empresa">
                            <FaBuilding aria-hidden />
                            {item.empresa_convidado || "Empresa não informada"}
                            {item.cargo_convidado
                              ? ` · ${item.cargo_convidado}`
                              : ""}
                          </div>
                        </div>
                        <div className="apl-escolhas-todas-item-tags">
                          <span
                            className={`apl-escolhas-todas-tag ${
                              prioritario
                                ? "apl-escolhas-todas-tag--prio"
                                : "apl-escolhas-todas-tag--dese"
                            }`}
                          >
                            {prioritario ? (
                              <FaStar aria-hidden />
                            ) : (
                              <FaThumbsUp aria-hidden />
                            )}
                            {labelTipo(item.tipo)}
                          </span>
                          <span className="apl-escolhas-todas-tag apl-escolhas-todas-tag--status">
                            {labelStatus(item.status)}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="apl-btn apl-btn--ghost"
          onClick={handleHide}
          disabled={loading}
        >
          <FaTimes size={14} aria-hidden />
          Fechar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalTodasEscolhasPatrocinadores;
